import { HistoriqueEvent } from '../../types';

// Point d'acces UNIQUE au flux d'historique de l'operation. Le backend ne fournit
// (pour l'instant) qu'un `prefix` textuel comme marqueur d'evenement : on le centralise
// ici derriere des selecteurs types et des constantes nommees, au lieu de disséminer des
// comparaisons de chaines dans les composants (anti-pattern legacy). Le jour ou le backend
// expose un type d'evenement, seul ce module change.
export const HISTORIQUE_PREFIX = {
  CANDIDATURE: 'Candidature',
  // Refus de la consultation par le PRESTATAIRE. A ne pas confondre avec 'Refusé' (rejet de
  // l'offre par l'acheteur a la cloture) : les deux posent la commande au statut 6, mais seul
  // 'Refus' veut dire "le candidat n'a pas repondu".
  REFUS: 'Refus',
  DECHIFFRAGE: 'Dechiffrage',
  CONSULTE: 'Consulte',
  CONSULTATION: 'Consultation',
  VALIDATION: 'Validation',
  ANALYSE_CLOTUREE: 'Analyse clôturée',
  NOTIFICATION: 'Notification',
  DEMANDE_SIGNATURE: 'Demande de signature',
  SIGNATURE_ACHETEUR: 'Signature acheteur',
  PROLONGATION: 'Prolongation',
} as const;

// Historique des prolongations du delai de reception des offres (plus recente en premier).
// Source : evenements journalises par le core sous le prefix 'Prolongation' (date + operateur + message).
export interface ProlongationInfo {
  date: string | null;
  operateur: string | null;
  message: string | null;
}
export const getProlongations = (historique?: HistoriqueEvent[]): ProlongationInfo[] =>
  (historique || [])
    .filter((h) => h.prefix === HISTORIQUE_PREFIX.PROLONGATION)
    .map((h) => ({ date: h.created_at ?? null, operateur: h.operateur ?? null, message: h.message ?? null }))
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

const findByPrefix = (
  historique: HistoriqueEvent[] | undefined,
  prefixes: readonly string[],
): HistoriqueEvent | undefined =>
  (historique || []).find((h) => h.prefix !== undefined && prefixes.includes(h.prefix));

export interface DechiffrageInfo {
  date: string | null;
  operateur: string | null;
}

// Info de dechiffrage des plis (date + operateur) ; absente tant que les plis sont scelles.
export const getDechiffrageInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.DECHIFFRAGE]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Info de cloture d'analyse (date + operateur). Le backend journalise la cloture MS sous le
// prefix 'Analyse clôturée' (les BC utilisent 'Validation') : on accepte les deux pour que le
// recap « clôturée le … par … » s'affiche. null tant que l'analyse n'est pas cloturee.
export const getClotureAnalyseInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.VALIDATION, HISTORIQUE_PREFIX.ANALYSE_CLOTUREE]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Info de lancement de la consultation (date + operateur=personne qui lance). Source : evenement
// journalise par lancerOperation cote core sous le prefix 'Consultation'. date null tant que la
// consultation n'est pas lancee (operation au brouillon). Alimente le recap « lancee le … par … ».
export const getLancementConsultationInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.CONSULTATION]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// Info de signature acheteur (date + operateur=organisme) depuis l'evenement 'Signature acheteur' ;
// null tant que le marche n'est pas signe. Source du « notifie au titulaire le {date} ».
export const getSignatureAcheteurInfo = (historique?: HistoriqueEvent[]): DechiffrageInfo => {
  const entry = findByPrefix(historique, [HISTORIQUE_PREFIX.SIGNATURE_ACHETEUR]);
  return { date: entry?.created_at ?? null, operateur: entry?.operateur ?? null };
};

// --- Depot d'une offre --------------------------------------------------------------------
//
// L'evenement 'Candidature' est journalise a la soumission du pli : c'est la SEULE preuve
// fiable qu'un candidat a repondu. Le statut de la commande n'en est pas une, il continue
// d'evoluer apres la reponse (5 -> 10 -> 11 quand le retenu signe, -> 6 quand l'offre est
// ecartee a la cloture) et se relirait a tort comme une absence de reponse.

// Trace du depot d'une offre : quand, et depuis quelle IP. Le NOM du deposant ne vient pas
// d'ici : l'evenement ne porte que la raison sociale de l'entite, l'ecran affiche la personne
// (commande.prestataire_signataire, resolue par le core).
export interface DepotOffre {
  date: string | null;
  ip: string | null;
}

// Trace du refus de la consultation par le prestataire (evenement 'Refus'). Meme remarque que
// ci-dessus sur le nom du refusant.
export interface RefusConsultation {
  date: string | null;
}

// Dernier evenement d'un prefix pour chaque commande (champ `destinataire`), indexe par id.
// On compare les dates au lieu de se fier a l'ordre du tableau : une re-soumission produit un
// second evenement, et c'est le plus recent qui porte la trace complete (les plus anciens sont
// anterieurs a la capture d'IP).
const dernierParCommande = (
  historique: HistoriqueEvent[] | undefined,
  prefix: string,
): Map<string, HistoriqueEvent> => {
  const parCommande = new Map<string, HistoriqueEvent>();
  (historique || []).forEach((h) => {
    if (h.prefix !== prefix || h.destinataire === null || h.destinataire === undefined) return;
    const commandeId = String(h.destinataire);
    const connu = parCommande.get(commandeId);
    if (!connu || (h.created_at ?? '') > (connu.created_at ?? '')) parCommande.set(commandeId, h);
  });
  return parCommande;
};

// Depots d'offre par id de commande. La presence d'une entree vaut reponse du candidat.
export const getDepotParCommande = (historique?: HistoriqueEvent[]): Record<string, DepotOffre> => {
  const depots: Record<string, DepotOffre> = {};
  dernierParCommande(historique, HISTORIQUE_PREFIX.CANDIDATURE).forEach((evt, commandeId) => {
    depots[commandeId] = { date: evt.created_at ?? null, ip: evt.ip_address || null };
  });
  return depots;
};

// Refus de consultation par id de commande.
export const getRefusParCommande = (
  historique?: HistoriqueEvent[],
): Record<string, RefusConsultation> => {
  const refus: Record<string, RefusConsultation> = {};
  dernierParCommande(historique, HISTORIQUE_PREFIX.REFUS).forEach((evt, commandeId) => {
    refus[commandeId] = { date: evt.created_at ?? null };
  });
  return refus;
};

// Date de candidature d'une commande (offre recue), par destinataire.
export const getCandidatureDate = (
  historique: HistoriqueEvent[] | undefined,
  commandeId: number | string | undefined,
): string | null => {
  if (commandeId === undefined || commandeId === null) return null;
  const depot = dernierParCommande(historique, HISTORIQUE_PREFIX.CANDIDATURE).get(String(commandeId));
  return depot?.created_at ?? null;
};

// Nombre de plis recus = prestataires DISTINCTS ayant depose une offre.
// Disponible des la consultation : l'evenement est journalise a la soumission, avant dechiffrage,
// donc l'acheteur voit la progression des depots meme plis scelles.
export const countPlisRecus = (historique?: HistoriqueEvent[]): number =>
  dernierParCommande(historique, HISTORIQUE_PREFIX.CANDIDATURE).size;

// L'analyse a-t-elle ete lancee (plis consultes) ? Marqueur d'historique consolide.
export const isAnalyzed = (historique?: HistoriqueEvent[]): boolean =>
  findByPrefix(historique, [HISTORIQUE_PREFIX.CONSULTE, HISTORIQUE_PREFIX.CONSULTATION]) !== undefined;

// L'analyse a-t-elle ete validee/cloturee (evenement 'Validation') ? Consomme par le gating
// des onglets et de la messagerie (source unique, plus de comparaison de chaine disséminee).
export const isAnalyseValidee = (historique?: HistoriqueEvent[]): boolean =>
  findByPrefix(historique, [HISTORIQUE_PREFIX.VALIDATION]) !== undefined;

// Date de notification d'une commande (retenu/non-retenu), par destinataire.
export const getNotificationDate = (
  historique: HistoriqueEvent[] | undefined,
  commandeId: number | string | undefined,
): string | null => {
  if (commandeId === undefined) return null;
  const entry = (historique || []).find(
    (h) => h.prefix === HISTORIQUE_PREFIX.NOTIFICATION && String(h.destinataire) === String(commandeId),
  );
  return entry?.created_at ?? null;
};

// Demandes de signature (acheteur simple -> signataire) presentes dans l'historique.
export const getDemandesSignature = (historique?: HistoriqueEvent[]): HistoriqueEvent[] =>
  (historique || []).filter((h) => h.prefix === HISTORIQUE_PREFIX.DEMANDE_SIGNATURE);
